import { useState } from "react";
import { useDispatch } from "react-redux";
import { Button, Input, InputNumber, Typography } from "antd";
import { createNoteAttribute } from "@store/actions/notes.actions";
import type { NoteAttribute } from "@store/reducers/notes.reducer";
import { readNoteApiError } from "@utils/noteApiError.mjs";
import { labelError, normalizeLabel, rangeError } from "@utils/notesForm.mjs";

interface NoteAttributeCreateFormProps {
    onCreated: (attribute: NoteAttribute) => void;
    onCancel: () => void;
    // The first two parts of the `data-attr` of the buttons, so each page logs its own clicks.
    dataAttrPrefix?: string;
}

export const NoteAttributeCreateForm = ({ onCreated, onCancel, dataAttrPrefix = "notes.note-dialog" }: NoteAttributeCreateFormProps) => {
    const dispatch = useDispatch<any>();
    const [name, setName] = useState("");
    const [min, setMin] = useState<number | null>(null);
    const [max, setMax] = useState<number | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const handleCreate = async () => {
        const clientError = labelError(name) ?? rangeError(min, max);
        setError(clientError);
        if (clientError) return;
        setSaving(true);
        try {
            const attribute = await dispatch(createNoteAttribute({ name: normalizeLabel(name), min_value: min, max_value: max }));
            onCreated(attribute);
        } catch (e) {
            // The thunk shows an error that has no field. An error with a field shows in this form.
            const { message: text, field } = readNoteApiError(e);
            if (field !== null) setError(text);
            setSaving(false);
        }
    };

    return (
        <div className="notes-dialog-create-attribute">
            <div className="notes-dialog-create-help">
                {'New attributes are shared with your whole team. Include the unit in the name, such as "Body Weight kg".'}
            </div>
            <div className="notes-dialog-create-row">
                <div className="notes-dialog-create-name">
                    <label className="notes-filter-label" htmlFor="notes-dialog-attribute-name">
                        Name
                    </label>
                    <Input id="notes-dialog-attribute-name" placeholder="e.g. Grade" value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div>
                    <label className="notes-filter-label" htmlFor="notes-dialog-attribute-min">
                        Min
                    </label>
                    <InputNumber id="notes-dialog-attribute-min" placeholder="No limit" value={min} onChange={setMin} />
                </div>
                <div>
                    <label className="notes-filter-label" htmlFor="notes-dialog-attribute-max">
                        Max
                    </label>
                    <InputNumber id="notes-dialog-attribute-max" placeholder="No limit" value={max} onChange={setMax} />
                </div>
                <Button type="primary" loading={saving} onClick={handleCreate} data-attr={`${dataAttrPrefix}.create-attribute`}>
                    Create
                </Button>
                <Button disabled={saving} onClick={onCancel} data-attr={`${dataAttrPrefix}.cancel-attribute`}>
                    Cancel
                </Button>
            </div>
            {error && <Typography.Text type="danger">{error}</Typography.Text>}
        </div>
    );
};
